import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { STATUS_BG_COLORS } from '../../utils/colors';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import PieChart from '@cloudscape-design/components/pie-chart';
import StatusIndicator from '../StatusIndicator';
import useUserProfile from 'src/hooks/useUserProfile';
import { useTasks } from 'src/store/tasks/useTasks';
import { Task } from 'src/types/helios/Task';
import { TaskStatus } from 'src/contstant/taskStatusColors';
import FilterButton from './FilterButton';
import { SearchHomeRequest } from './HomeFilter';
import { Spinner } from '@cloudscape-design/components';
import { calculatePercentage } from 'src/utils/generalUtils';
import _ from 'lodash';

interface TaskMetrics {
  assignedTasks: number;
  inProgressTasks: number;
  overdueTasks: number;
  completedTasks: number;
  cancelledTasks: number;
  totalTasks: number;
}
const TaskStatusSection = ({}) => {
  const { getUserBusinessUnitId, getLocations, currentUser, getLanguage } =
    useUserProfile();

  const { getTaskStats, taskStats, getTaskApiStatus } = useTasks();
  const [searchParams, setSearchParams] = useState<SearchHomeRequest>({});

  // Track business unit to detect changes
  const businessUnitId = getUserBusinessUnitId();

  useEffect(() => {
    if (businessUnitId) {
      getTaskStats({
        ...searchParams,
        businessUnitId: businessUnitId,
        locations: getLocations(),
        taskStatuses: [
          TaskStatus.ASSIGNED,
          TaskStatus.IN_PROGRESS,
          TaskStatus.OVERDUE,
          TaskStatus.CANCELLED,
          TaskStatus.COMPLETED,
        ],
      });
    }
  }, [businessUnitId, currentUser, searchParams, JSON.stringify(getLocations())]);

  const getTaskMetric = (): TaskMetrics => {
    const statuses = _.compact(
      _.flatMap(taskStats?.taskTypes, (taskType) =>
        _.map(taskType?.divisions, (division) => division?.statuses),
      ),
    );

    const assignedTasks = _.sumBy(statuses, (s) => s?.ASSIGNED || 0);
    const inProgressTasks = _.sumBy(statuses, (s) => s?.IN_PROGRESS || 0);
    const overdueTasks = _.sumBy(statuses, (s) => s?.OVERDUE || 0);
    const completedTasks = _.sumBy(statuses, (s) => s?.COMPLETED || 0);
    const cancelledTasks = _.sumBy(statuses, (s) => s?.CANCELLED || 0);

    return {
      assignedTasks,
      inProgressTasks,
      overdueTasks,
      completedTasks,
      cancelledTasks,
      totalTasks:
        assignedTasks + inProgressTasks + overdueTasks + completedTasks + cancelledTasks,
    };
  };

  const taskMeterics = getTaskMetric();

  const chartData = [
    {
      title: 'Completed',
      value: taskMeterics.completedTasks,
      color: STATUS_BG_COLORS.COMPLETED,
    },
    {
      title: 'In Progress',
      value: taskMeterics.inProgressTasks,
      color: STATUS_BG_COLORS.IN_PROGRESS,
    },
    {
      title: 'Assigned',
      value: taskMeterics.assignedTasks,
      color: STATUS_BG_COLORS.ASSIGNED,
    },
    {
      title: 'Overdue',
      value: taskMeterics.overdueTasks,
      color: STATUS_BG_COLORS.OVERDUE,
    },
    {
      title: 'Cancelled',
      value: taskMeterics.cancelledTasks,
      color: STATUS_BG_COLORS.CANCELLED,
    },
  ];

  return (
    <Container
      data-testid="task-status-container"
      header={
        <Header
          data-testid="task-status-header"
          variant="h2"
          actions={
            <FilterButton
              data-testid="task-filter-button"
              onSearchChange={function (searchParams: SearchHomeRequest): void {
                setSearchParams(searchParams);
              }}
              searchParams={searchParams}
              type={'task'}
            />
          }
        >
          Task Overview
        </Header>
      }
    >
      <StatusIndicator
        data-testid="total-tasks-indicator"
        value={taskMeterics.totalTasks}
        label="Total Tasks"
        variant="horizontal"
        backgroundColor="bg-color-assigned"
        textColor="color-assigned"
        loading={getTaskApiStatus.isLoading}
      />
      <div className="status-wrapper" data-testid="status-wrapper">
        <StatusIndicator
          data-testid="completed-tasks-indicator"
          value={taskMeterics.completedTasks}
          label="Completed"
          variant="vertical"
          backgroundColor="bg-color-completed"
          textColor="color-completed"
          loading={getTaskApiStatus.isLoading}
        />
        <StatusIndicator
          data-testid="in-progress-tasks-indicator"
          value={taskMeterics.inProgressTasks}
          label="In Progress"
          variant="vertical"
          backgroundColor="bg-color-in-progress"
          textColor="color-in-progress"
          loading={getTaskApiStatus.isLoading}
        />
        <StatusIndicator
          data-testid="assigned-tasks-indicator"
          value={taskMeterics.assignedTasks}
          label="Assigned"
          variant="vertical"
          backgroundColor="bg-color-assigned"
          textColor="color-assigned"
          loading={getTaskApiStatus.isLoading}
        />

        <StatusIndicator
          data-testid="overdue-tasks-indicator"
          value={taskMeterics.overdueTasks}
          label="Overdue"
          variant="vertical"
          backgroundColor="bg-color-overdue"
          textColor="color-overdue"
          loading={getTaskApiStatus.isLoading}
        />

        <StatusIndicator
          data-testid="cancelled-tasks-indicator"
          value={taskMeterics.cancelledTasks}
          label="Cancelled"
          variant="vertical"
          backgroundColor="bg-color-cancelled"
          textColor="color-cancelled"
          loading={getTaskApiStatus.isLoading}
        />
      </div>

      <div
        data-testid="chart-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '400px',
        }}
      >
        {getTaskApiStatus.isLoading ? (
          <Box data-testid="loading-spinner" textAlign="center" color="inherit">
            <Spinner data-testid="task-spinner" />
          </Box>
        ) : taskMeterics.totalTasks > 0 ? (
          <PieChart
            data-testid="task-pie-chart"
            data={chartData}
            size="medium"
            hideFilter
            hideLegend
            segmentDescription={(datum, sum) =>
              `${datum.value} tasks, ${calculatePercentage(datum.value, sum)}% of total`
            }
            i18nStrings={{
              detailsValue: 'Value',
              detailsPercentage: 'Percentage',
              filterLabel: 'Filter',
              filterPlaceholder: 'Filter tasks',
              filterSelectedAriaLabel: 'selected',
              detailPopoverDismissAriaLabel: 'Dismiss',
              legendAriaLabel: 'Legend',
              chartAriaRoleDescription: 'Pie chart',
            }}
          />
        ) : (
          <Box data-testid="no-data-message" textAlign="center" color="inherit">
            <b>No task data available</b>
          </Box>
        )}
      </div>
    </Container>
  );
};

export default TaskStatusSection;
